import React, { useState, useEffect, useContext } from "react";
import { gql, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import StarekContext from "../context/StarekContext";
import LiveProductionMonitor from "../pages/report/LiveProductionMonitor";

const GET_SHIFT_LIST_FOR_PLANT = gql`
    query GetShifts($plantId: Int!) {
        shifts(where: { plant_id: { equals: $plantId } }) {
            id
            name
            description
            start_time
            end_time
            runs_on
            created_at
        }
    }
`;

function EfficiencyMonitor(props) {
    const { lastUsedPlant } = useContext(StarekContext);
    const [activeTab, setActiveTab] = useState("running");
    const [collapsed, setCollapsed] = useState(false);
    const [filters, setFilters] = useState({});
    const [range, setRange] = useState([0, 0]);
    const [selectedDeviceIndex, setSelectedDeviceIndex] = useState(0);

    const { data: shiftData } = useQuery(GET_SHIFT_LIST_FOR_PLANT, {
        variables: { plantId: lastUsedPlant?.id },
        skip: !lastUsedPlant?.id,
    });

    useEffect(() => {
        setSelectedDeviceIndex(0);
    }, [activeTab]);

    useEffect(() => {
        if (!shiftData?.shifts || !props.deviceData?.shadowDevices) return;

        const now = new Date();
        const currentSeconds =
            now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

        const getShiftTimes = (shift, baseDateStr) => {
            const start = new Date(baseDateStr + "T00:00:00");
            const end = new Date(baseDateStr + "T00:00:00");
            start.setSeconds(shift.start_time);
            end.setSeconds(shift.end_time);
            if (shift.end_time < shift.start_time) {
                end.setDate(end.getDate() + 1);
            }
            return { start, end };
        };

        const getDateStr = (daysAgo = 0) => {
            const d = new Date(now);
            d.setDate(d.getDate() - daysAgo);
            return d.toISOString().slice(0, 10);
        };

        const shiftRunsOn = (shift, dateStr) => {
            if (!shift.runs_on) return true; // no restriction
            const dayOfWeek = new Date(dateStr + "T12:00:00").getDay(); // 0=Sun
            return (shift.runs_on & (1 << dayOfWeek)) !== 0;
        };

        const sortedShifts = [...shiftData.shifts].sort(
            (a, b) => a.start_time - b.start_time,
        );

        let targetShift = null;
        let selectedDateStr = getDateStr(0);

        if (activeTab === "running") {
            for (const shift of sortedShifts) {
                const todayStr = getDateStr(0);
                const yesterdayStr = getDateStr(1);

                if (shift.start_time < shift.end_time) {
                    if (
                        shiftRunsOn(shift, todayStr) &&
                        currentSeconds >= shift.start_time &&
                        currentSeconds <= shift.end_time
                    ) {
                        targetShift = shift;
                        selectedDateStr = todayStr;
                        break;
                    }
                } else {
                    if (
                        shiftRunsOn(shift, todayStr) &&
                        currentSeconds >= shift.start_time
                    ) {
                        targetShift = shift;
                        selectedDateStr = todayStr;
                        break;
                    }
                    if (
                        shiftRunsOn(shift, yesterdayStr) &&
                        currentSeconds <= shift.end_time
                    ) {
                        targetShift = shift;
                        selectedDateStr = yesterdayStr;
                        break;
                    }
                }
            }
        } else {
            const allCompletedInstances = [];

            for (let daysAgo = 0; daysAgo <= 7; daysAgo++) {
                const dateStr = getDateStr(daysAgo);
                for (const shift of sortedShifts) {
                    if (!shiftRunsOn(shift, dateStr)) continue;
                    const { end } = getShiftTimes(shift, dateStr);
                    if (end < now) {
                        allCompletedInstances.push({
                            shift,
                            dateStr,
                            endTimeMs: end.getTime(),
                        });
                    }
                }
                if (allCompletedInstances.length > 0 && daysAgo > 0) break;
            }

            allCompletedInstances.sort((a, b) => b.endTimeMs - a.endTimeMs);

            if (allCompletedInstances.length > 0) {
                targetShift = allCompletedInstances[0].shift;
                selectedDateStr = allCompletedInstances[0].dateStr;
            }
        }

        if (targetShift) {
            let diff = targetShift.end_time - targetShift.start_time;
            if (diff < 0) diff += 86400;

            const base = new Date(selectedDateStr);
            const startDate = new Date(base);
            const endDate = new Date(base);

            startDate.setSeconds(targetShift.start_time);
            endDate.setSeconds(targetShift.end_time);

            if (targetShift.end_time < targetShift.start_time) {
                endDate.setDate(endDate.getDate() + 1);
            }

            setFilters({
                devices: props.deviceData.shadowDevices
                    .map((d) => d.id)
                    .join(","),
                duration: `shift_${targetShift.id}`,
                shiftDate: selectedDateStr,
                durationStart: startDate.toISOString(),
                durationEnd: endDate.toISOString(),
                durationSeconds: diff,
            });
            setRange([0, diff]);
        }
    }, [shiftData, props.deviceData, activeTab]);

    return (
        <div
            className={`card card-outline card-info ${collapsed ? "collapsed-card" : ""}`}
            style={{ height: collapsed ? "auto" : "400px" }}
        >
            <div
                className="card-header ui-sortable-handle"
                style={{ cursor: "move" }}
            >
                <h3 className="card-title">
                    <i className="fas fa-clipboard mr-1" />
                    Live Production Monitor
                </h3>
                <div className="card-tools d-flex align-items-center">
                    <ul className="nav nav-pills mb-0">
                        <li className="nav-item">
                            <a
                                className={`nav-link ${
                                    activeTab === "running" ? "active" : ""
                                }`}
                                href="#revenue-chart"
                                data-toggle="tab"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab("running");
                                }}
                            >
                                Running Shift
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={`nav-link ${
                                    activeTab === "completed" ? "active" : ""
                                }`}
                                href="#sales-chart"
                                data-toggle="tab"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab("completed");
                                }}
                            >
                                Last Completed Shift
                            </a>
                        </li>
                    </ul>
                    <button
                        type="button"
                        className="btn btn-tool ml-2"
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        <i
                            className={`fas ${collapsed ? "fa-plus" : "fa-minus"}`}
                        ></i>
                    </button>
                </div>
            </div>
            <div className={`card-body ${collapsed ? "d-none" : ""}`}>
                <LiveProductionMonitor
                    deviceData={props.deviceData}
                    filters={filters}
                    range={range}
                    hideControls={true}
                    selectedDeviceIndex={selectedDeviceIndex}
                    onSelectedDeviceIndexChange={setSelectedDeviceIndex}
                />
            </div>
            <div className={`card-footer ${collapsed ? "d-none" : ""}`}>
                <div className="tw-flex tw-justify-end">
                    <Link
                        to="/reports"
                        className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-800 tw-no-underline hover:tw-underline tw-flex tw-items-center tw-gap-1"
                    >
                        See Historical Production...
                        <i className="fas fa-arrow-right tw-text-xs"></i>
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default EfficiencyMonitor;
